import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Line,
  CartesianGrid,
  ComposedChart,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Tag, Gift, DollarSign, TrendingDown } from "lucide-react";
import {
  CouponApi,
  CouponSummary,
  MonthlySummary,
  PerformingCouponSummary,
  NewReturningCustomersSummary,
} from "@/api/giftcard.api";
import { useToast } from "@/hooks/use-toast";
import { HandleError } from "@/api/axiosClient";

interface CouponsAnalyticsProps {
  onLoad?: () => void;
}

export function CouponsAnalytics({ onLoad }: CouponsAnalyticsProps) {
  const [analyticsData, setAnalyticsData] = useState<CouponSummary | null>(
    null
  );
  const [monthlySummary, setMonthlySummary] = useState<MonthlySummary[]>([]);
  const [performingCoupons, setPerformingCoupons] = useState<
    PerformingCouponSummary[]
  >([]);
  const [newReturningCustomers, setNewReturningCustomers] =
    useState<NewReturningCustomersSummary | null>(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const { toast } = useToast();

  const fetchAnalytics = async () => {
    setIsLoadingAnalytics(true);
    try {
      const response = await CouponApi.getCouponAnalyticsReport();
      if (response.couponSummary && response.couponSummary.length > 0) {
        setAnalyticsData(response.couponSummary[0]);
      } else {
        setAnalyticsData(null);
      }
      if (response.monthlySummary && Array.isArray(response.monthlySummary)) {
        setMonthlySummary(response.monthlySummary);
      } else {
        setMonthlySummary([]);
      }
      if (
        response.performingCouponSummary &&
        Array.isArray(response.performingCouponSummary)
      ) {
        setPerformingCoupons(response.performingCouponSummary);
      } else {
        setPerformingCoupons([]);
      }
      if (
        response.newReturningCustomersSummary &&
        response.newReturningCustomersSummary.length > 0
      ) {
        setNewReturningCustomers(response.newReturningCustomersSummary[0]);
      } else {
        setNewReturningCustomers(null);
      }
      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Warning",
          description: response.error_msg,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to fetch analytics",
        variant: "destructive",
      });
      setAnalyticsData(null);
      setMonthlySummary([]);
      setPerformingCoupons([]);
      setNewReturningCustomers(null);
    } finally {
      setIsLoadingAnalytics(false);
      if (onLoad) onLoad();
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Coupons Issued
            </CardTitle>
            <div className="bg-blue-100 p-2 rounded-md">
              <Tag className="h-4 w-4 text-blue-500" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoadingAnalytics
                ? "..."
                : analyticsData
                ? analyticsData.couponsIssued.toLocaleString()
                : "0"}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Coupons Redeemed
            </CardTitle>
            <div className="bg-green-100 p-2 rounded-md">
              <Gift className="h-4 w-4 text-green-500" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoadingAnalytics
                ? "..."
                : analyticsData
                ? analyticsData.couponsRedeemed.toLocaleString()
                : "0"}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Revenue Generated
            </CardTitle>
            <div className="bg-purple-100 p-2 rounded-md">
              <DollarSign className="h-4 w-4 text-purple-500" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoadingAnalytics
                ? "..."
                : analyticsData
                ? `$${analyticsData.revenueGenerated.toLocaleString()}`
                : "$0"}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Discount Cost</CardTitle>
            <div className="bg-orange-100 p-2 rounded-md">
              <TrendingDown className="h-4 w-4 text-orange-500" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoadingAnalytics
                ? "..."
                : analyticsData
                ? `$${analyticsData.discountCost.toLocaleString()}`
                : "$0"}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Coupon Issued vs Redeemed Chart */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Coupon Issued vs Redeemed - Month on Month</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] w-full">
            {monthlySummary.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={monthlySummary.map((item) => ({
                    month: item.month,
                    issued: item.issued,
                    redeemed: item.redeemed,
                    redemptionRate: item.redeemptionRate,
                  }))}
                  margin={{ top: 20, right: 50, left: 20, bottom: 20 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f0f0f0"
                  />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      if (value >= 1000) {
                        return `${(value / 1000).toFixed(0)}K`;
                      }
                      return value.toString();
                    }}
                    label={{
                      value: "Count",
                      angle: -90,
                      position: "insideLeft",
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `${value}%`}
                    label={{
                      value: "Redemption Rate",
                      angle: 90,
                      position: "right",
                      offset: 5,
                      style: { textAnchor: "middle" },
                    }}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                            <p className="font-semibold mb-2">{data.month}</p>
                            <p className="text-sm text-blue-600">
                              Issued: {data.issued.toLocaleString()}
                            </p>
                            <p className="text-sm text-green-600">
                              Redeemed: {data.redeemed.toLocaleString()}
                            </p>
                            <p className="text-sm text-blue-500">
                              Redemption Rate: {data.redemptionRate.toFixed(1)}%
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend
                    formatter={(value) => {
                      if (value === "issued") return "Coupons Issued";
                      if (value === "redeemed") return "Coupons Redeemed";
                      if (value === "redemptionRate")
                        return "Redemption Rate ↑";
                      return value;
                    }}
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="issued"
                    fill="#3b82f6"
                    name="issued"
                    radius={[4, 4, 0, 0]}
                    barSize={30}
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="redeemed"
                    fill="#10b981"
                    name="redeemed"
                    radius={[4, 4, 0, 0]}
                    barSize={30}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="redemptionRate"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ fill: "#3b82f6", r: 5 }}
                    activeDot={{ r: 7 }}
                    name="redemptionRate"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                {isLoadingAnalytics ? "Loading..." : "No data available"}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Top Performing Coupons and Second Card */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Top Performing Coupons Table */}
        <Card>
          <CardHeader>
            <CardTitle>Top Performing Coupons</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                      Coupon Rule
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                      Type
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                      Issued
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                      Redeemed
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                      Redemption
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {performingCoupons.length > 0 ? (
                    performingCoupons.map((coupon, index) => (
                      <tr
                        key={index}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="py-3 px-4 text-sm text-gray-900">
                          {coupon.couponName}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700">
                          {coupon.type}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700">
                          {coupon.issued.toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700">
                          {coupon.redeemed.toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-sm font-semibold text-gray-900">
                          {coupon.redemption}%
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        className="py-8 px-4 text-center text-sm text-gray-500"
                      >
                        {isLoadingAnalytics
                          ? "Loading..."
                          : "No data available"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* New vs Returning Customers Card */}
        <Card>
          <CardHeader>
            <CardTitle>New vs Returning Customers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-6">
              {/* Left Section - List */}
              <div className="flex-1 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">New</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {newReturningCustomers
                      ? `${newReturningCustomers.newCustomers}%`
                      : isLoadingAnalytics
                      ? "..."
                      : "0%"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Returning</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {newReturningCustomers
                      ? `${newReturningCustomers.returningCustomers}%`
                      : isLoadingAnalytics
                      ? "..."
                      : "0%"}
                  </span>
                </div>
              </div>

              {/* Right Section - Donut Chart */}
              <div className="flex-1">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={
                        newReturningCustomers
                          ? [
                              {
                                name: "Returning",
                                value: newReturningCustomers.returningCustomers,
                                color: "#3b82f6",
                              },
                              {
                                name: "New",
                                value: newReturningCustomers.newCustomers,
                                color: "#10b981",
                              },
                            ]
                          : [
                              {
                                name: "Returning",
                                value: 0,
                                color: "#3b82f6",
                              },
                              { name: "New", value: 0, color: "#10b981" },
                            ]
                      }
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      startAngle={90}
                      endAngle={-270}
                      dataKey="value"
                    >
                      {(newReturningCustomers
                        ? [
                            {
                              name: "Returning",
                              value: newReturningCustomers.returningCustomers,
                              color: "#3b82f6",
                            },
                            {
                              name: "New",
                              value: newReturningCustomers.newCustomers,
                              color: "#10b981",
                            },
                          ]
                        : [
                            {
                              name: "Returning",
                              value: 0,
                              color: "#3b82f6",
                            },
                            { name: "New", value: 0, color: "#10b981" },
                          ]
                      ).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <text
                      x="50%"
                      y="50%"
                      textAnchor="middle"
                      dominantBaseline="central"
                      className="text-2xl font-bold fill-gray-900"
                      dy="-7"
                    >
                      {newReturningCustomers
                        ? `${newReturningCustomers.returningCustomers}%`
                        : isLoadingAnalytics
                        ? "..."
                        : "0%"}
                    </text>
                    <Legend
                      verticalAlign="bottom"
                      align="center"
                      iconType="circle"
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
